import { PureComponent } from 'react';
import _ from 'lodash';

import { IWithFeatureFlagProps, withFeatureFlags } from '@tock/shared/utils/featureFlagContext';
import {
  IconBookmarkCheckmark,
  IconBubbleExclamation,
  IconBubbleMessage,
  IconDelivery,
  IconLogoCommerce7,
  IconLogoWineDirect,
  IconNotAllowed,
  IconNote,
  IconPayment,
  IconPeopleTeam,
  IconPlusCircle,
  IconQrCode,
  IconQuestionCircle,
  IconSignature,
  IconTag,
  IconVerified,
} from '@tock/icons';
import {
  NOTESCOPE,
  guestDietaryOrGroupGuestDietaryNotes,
  guestOrGroupGuestNotes,
} from 'utils/purchaseUtils';
import { Theme, css } from '@emotion/react';
import { Typography } from 'bento';
import { dashboard } from 'types';
import { getGuestIntegrationInformation } from 'utils/guestIntegrationsUtils';
import { hasRelevantTockProfileInfo } from 'utils/guestProfileUtils';
import { mapStringToGuestTagsSelector, mapStringToVisitTagsSelector } from 'selectors/tagSelectors';
import { oc } from 'ts-optchain';
import NeedsNameMapping from 'constants/service/needsNameMapping';
import TagIconsList from 'components/crm/TagIconsList';
import TagTypes from 'constants/TagTypes';
import connect, { DefaultConnectProps } from '@tock/shared/utils/connect';

interface ConnectProps extends DefaultConnectProps {
  activeBusinessId: number;
  processedVisitTags: dashboard.Tag[];
  processedGuestTags: dashboard.Tag[];
}

interface Props {
  guestProfile?: dashboard.GuestProfile;
  visitTags: string[];
  quickTags?: string[];
  hasBeenConfirmed?: boolean;
  hasQuickNote: boolean;
  hasVisitNote: boolean;
  visitNote?: string;
  hasVisitDietaryNote: boolean;
  hasMobileTicket?: boolean;
  hasPaymentRequired: boolean;
  hasPurchasedSupplements: boolean;
  hasNewMessage: boolean;
  hasAnsweredQuestion: boolean;
  hasDelivered?: boolean;
  lastTextMessageStatus?: dashboard.TextMessage.TextStatus;
  maxIconsToDisplay?: number;
  hasDeliverySignatureRequirement?: boolean;
  noParentWrapper?: boolean;
  renderDot?: boolean;
  isPartyCard?: boolean;
}

enum IconTypes {
  MOBILE_TICKET = 'mobileTicket',
  CONFIRMED = 'confirmed',
  TEXT_MESSAGE = 'textMessage',
  PAYMENT = 'payment',
  QUICK_TAGS = 'quickTags',
  GUEST_TAGS = 'guestTags',
  VISIT_TAGS = 'visitTags',
  WINE_DIRECT = 'wineDirect',
  COMMERCE_7 = 'commerce7',
  SALESFORCE = 'salesforce',
  BLOYAL = 'bloyal',
  SUPPLEMENTS = 'supplements',
  ANSWERED_QUESTIONS = 'answeredQuestions',
  TOCK_PROFILE = 'tockProfile',
  NOTE = 'note',
  DIETARY_NOTE = 'dietaryNote',
  DELIVERED = 'delivered',
  SIGNATURE = 'signature',
  OTHER = 'other',
}
const cssDefs = {
  iconCss:
    (shouldApply: boolean) =>
    ({ colors }: Theme) => css`
      cursor: inherit;
      ${shouldApply &&
      `
        color: ${colors.fgSubtle};
        height: 16px;
        margin-right: 4px;
        width: 16px;
        vertical-align: middle;
      `}
    `,
};

class GuestAndVisitIcons extends PureComponent<Props & ConnectProps & IWithFeatureFlagProps> {
  getIconComponents() {
    // Return a single icon or array of icons for each key in the IconTypes enum
    const {
      processedVisitTags,
      processedGuestTags,
      hasNewMessage,
      lastTextMessageStatus,
      isPartyCard,
    } = this.props;

    const showNewTextMessageIndicator = lastTextMessageStatus;
    const iconCss = cssDefs.iconCss(isPartyCard ?? false);

    return {
      [IconTypes.MOBILE_TICKET]: (
        <IconQrCode
          key={IconTypes.MOBILE_TICKET}
          data-for="tooltip"
          data-tip="Mobile ticket"
          css={iconCss}
        />
      ),
      [IconTypes.CONFIRMED]: (
        <IconBookmarkCheckmark
          key={IconTypes.CONFIRMED}
          data-for="tooltip"
          data-tip="Guest confirmed"
          css={(theme: Theme) => css`
            ${iconCss(theme)};
            color: var(--color-confirmed);
          `}
        />
      ),
      [IconTypes.TEXT_MESSAGE]: showNewTextMessageIndicator
        ? this.renderTextMessageIndicator()
        : hasNewMessage && (
            <IconBubbleMessage
              key={IconTypes.TEXT_MESSAGE}
              data-for="tooltip"
              data-tip="Unread message"
              css={(theme: Theme) => css`
                ${iconCss(theme)};
                color: var(--color-message-legacy);
              `}
            />
          ),
      [IconTypes.PAYMENT]: (
        <IconPayment
          key={IconTypes.PAYMENT}
          data-for="tooltip"
          data-tip="Payment due"
          css={(theme: Theme) => css`
            ${iconCss(theme)};
            color: ${theme.colors.fgDangerBase};
          `}
        />
      ),
      [IconTypes.QUICK_TAGS]: this.renderQuickTags(),
      [IconTypes.GUEST_TAGS]: (
        <TagIconsList
          key={IconTypes.GUEST_TAGS}
          tags={processedGuestTags}
          type={TagTypes.GUEST}
          isPartyCard={isPartyCard}
        />
      ),
      [IconTypes.VISIT_TAGS]: (
        <TagIconsList
          key={IconTypes.VISIT_TAGS}
          tags={processedVisitTags}
          type={TagTypes.VISIT}
          isPartyCard={isPartyCard}
        />
      ),
      [IconTypes.WINE_DIRECT]: (
        <IconLogoWineDirect
          key={IconTypes.WINE_DIRECT}
          data-for="tooltip"
          data-tip="WineDirect club membership"
          css={iconCss}
        />
      ),
      [IconTypes.COMMERCE_7]: (
        <IconLogoCommerce7
          key={IconTypes.COMMERCE_7}
          data-for="tooltip"
          data-tip="Commerce 7 wine club membership"
          css={iconCss}
        />
      ),
      [IconTypes.SALESFORCE]: (
        <IconPeopleTeam
          key={IconTypes.SALESFORCE}
          data-for="tooltip"
          data-tip="Salesforce membership"
          css={iconCss}
        />
      ),
      [IconTypes.BLOYAL]: (
        <IconPeopleTeam
          key={IconTypes.BLOYAL}
          data-for="tooltip"
          data-tip="bLoyal loyalty program"
          css={iconCss}
        />
      ),
      [IconTypes.SUPPLEMENTS]: (
        <IconPlusCircle
          key={IconTypes.SUPPLEMENTS}
          data-for="tooltip"
          data-tip="Supplement purchased"
          css={iconCss}
        />
      ),
      [IconTypes.ANSWERED_QUESTIONS]: (
        <IconQuestionCircle
          key={IconTypes.ANSWERED_QUESTIONS}
          data-for="tooltip"
          data-tip="Guest responses"
          css={iconCss}
        />
      ),
      [IconTypes.TOCK_PROFILE]: (
        <IconVerified
          key={IconTypes.TOCK_PROFILE}
          data-for="tooltip"
          data-tip="Tock profile"
          css={iconCss}
        />
      ),
      [IconTypes.NOTE]: (
        <IconNote key={IconTypes.NOTE} data-for="tooltip" data-tip="Notes" css={iconCss} />
      ),
      [IconTypes.DIETARY_NOTE]: (
        <IconNotAllowed
          key={IconTypes.DIETARY_NOTE}
          data-for="tooltip"
          data-tip="Dietary notes"
          css={iconCss}
        />
      ),
      [IconTypes.DELIVERED]: (
        <IconDelivery
          key={IconTypes.DELIVERED}
          data-for="tooltip"
          data-tip="Delivered"
          css={(theme: Theme) => css`
            ${iconCss(theme)};
            color: var(--color-confirmed);
          `}
        />
      ),
      [IconTypes.SIGNATURE]: (
        <IconSignature data-for="tooltip" data-tip="Signature required" css={iconCss} />
      ),
      [IconTypes.OTHER]: (
        <IconTag key={IconTypes.OTHER} data-for="tooltip" data-tip="Tags applied" css={iconCss} />
      ),
    };
  }
  renderTextMessageIndicator() {
    const { lastTextMessageStatus, hasNewMessage, isPartyCard } = this.props;
    const iconCss = cssDefs.iconCss(isPartyCard ?? false);

    switch (lastTextMessageStatus) {
      case 'SENT':
        return (
          <IconBubbleMessage
            key={IconTypes.TEXT_MESSAGE + 'Sent'}
            data-for="tooltip"
            data-tip="Sent message"
            css={(theme: Theme) => css`
              ${iconCss(theme)};
              color: var(--color-message-sent);
            `}
          />
        );
      case 'RECEIVED':
        return (
          <IconBubbleExclamation
            key={IconTypes.TEXT_MESSAGE + 'Received'}
            data-for="tooltip"
            data-tip={hasNewMessage ? 'Unread message' : 'Received message'}
            css={(theme: Theme) => css`
              ${iconCss(theme)};
              color: ${hasNewMessage ? 'var(--color-message)' : 'var(--icon-color-default)'};
            `}
          />
        );
      default:
        return null;
    }
  }

  renderQuickTags() {
    const { quickTags, isPartyCard } = this.props;
    const iconCss = cssDefs.iconCss(isPartyCard ?? false);

    return !quickTags
      ? []
      : quickTags.reduce<JSX.Element[]>((tagArray, tag, idx) => {
          const Icon = NeedsNameMapping[tag]?.newIcon;

          if (Icon) {
            tagArray.push(
              <Icon
                key={IconTypes.QUICK_TAGS + idx}
                data-for="tooltip"
                data-tip={tag}
                css={(theme: Theme) => css`
                  ${iconCss(theme)};
                  color: ${theme.colors.fgPrimaryBase};
                `}
              />
            );
          }

          return tagArray;
        }, []);
  }

  render() {
    const {
      guestProfile,
      activeBusinessId,
      processedVisitTags,
      processedGuestTags,
      hasBeenConfirmed,
      hasQuickNote,
      hasVisitNote,
      hasVisitDietaryNote,
      hasMobileTicket,
      hasPaymentRequired,
      hasPurchasedSupplements,
      hasNewMessage,
      hasAnsweredQuestion,
      hasDelivered,
      quickTags,
      lastTextMessageStatus,
      maxIconsToDisplay,
      hasDeliverySignatureRequirement,
      noParentWrapper,
      renderDot,
    } = this.props;

    const attributes = oc(guestProfile).attribute([]);

    const isWineDirectMember = !_.isEmpty(
      getGuestIntegrationInformation('WINEDIRECT_CLASSIC', attributes).membership
    );
    const isSalesforceCRMMember = !_.isEmpty(
      getGuestIntegrationInformation('SALESFORCE', attributes).membership
    );
    const isBloyalCRMMember = !_.isEmpty(
      getGuestIntegrationInformation('BLOYAL', attributes).membership
    );

    const isCommerce7Member = !!getGuestIntegrationInformation('COMMERCE7', attributes)?.membership;

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const hasGuestNote = !_.isEmpty(guestOrGroupGuestNotes(guestProfile!, activeBusinessId));
    const useGroupDietaryNote = this.props.isFeatureEnabled('GROUP_LEVEL_DIETARY_NOTES');
    const hasGuestDietaryNote = !_.isEmpty(
      guestDietaryOrGroupGuestDietaryNotes(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        guestProfile!,
        activeBusinessId,
        useGroupDietaryNote ? NOTESCOPE.BUSINESS_GROUP : NOTESCOPE.BUSINESS
      )
    );

    const hasNote = hasQuickNote || hasGuestNote || hasVisitNote;
    const hasDietaryNote = hasGuestDietaryNote || hasVisitDietaryNote;

    const tockProfile = guestProfile && hasRelevantTockProfileInfo(guestProfile);

    const visitTagCount = _.filter(processedVisitTags, (tag) => tag.icon).length;
    const guestTagCount = _.filter(processedGuestTags, (tag) => tag.icon).length;

    const hasOtherTags =
      processedVisitTags.some((tag) => !tag.icon) || processedGuestTags.some((tag) => !tag.icon);

    const showNewTextMessageIndicator = lastTextMessageStatus;

    const iconVisibilityMapping = {
      [IconTypes.MOBILE_TICKET]: hasMobileTicket,
      [IconTypes.DELIVERED]: hasDelivered,
      [IconTypes.CONFIRMED]: hasBeenConfirmed,
      [IconTypes.TEXT_MESSAGE]: showNewTextMessageIndicator || hasNewMessage,
      [IconTypes.PAYMENT]: hasPaymentRequired,
      [IconTypes.QUICK_TAGS]: quickTags && quickTags.length,
      [IconTypes.GUEST_TAGS]: guestTagCount,
      [IconTypes.VISIT_TAGS]: visitTagCount,
      [IconTypes.WINE_DIRECT]: isWineDirectMember,
      [IconTypes.COMMERCE_7]: isCommerce7Member,
      [IconTypes.SALESFORCE]: isSalesforceCRMMember,
      [IconTypes.BLOYAL]: isBloyalCRMMember,
      [IconTypes.SUPPLEMENTS]: hasPurchasedSupplements,
      [IconTypes.ANSWERED_QUESTIONS]: hasAnsweredQuestion,
      [IconTypes.TOCK_PROFILE]: tockProfile,
      [IconTypes.NOTE]: hasNote,
      [IconTypes.DIETARY_NOTE]: hasDietaryNote,
      [IconTypes.SIGNATURE]: hasDeliverySignatureRequirement,
      [IconTypes.OTHER]: hasOtherTags,
    };

    // Determine which icon types we need to render, filter out falsey valyes
    const visibleIconTypes = Object.keys(iconVisibilityMapping).filter(
      (val) => iconVisibilityMapping[val as IconTypes]
    );

    // Get out if no icon types are visible
    if (!visibleIconTypes.length) {
      return null;
    }

    // Get a mapping of types to icons and then create a flat array of JSX
    // elements to render
    const iconComponentMap = this.getIconComponents();
    const iconsToRender = visibleIconTypes.reduce<JSX.Element[]>((acc, key) => {
      const newElements = iconComponentMap[key as IconTypes];

      if (!newElements) {
        return acc;
      }

      return _.isArray(newElements) ? [...acc, ...newElements] : [...acc, newElements];
    }, []);

    const renderIcons = () => {
      return maxIconsToDisplay && iconsToRender.length > maxIconsToDisplay
        ? iconsToRender
            .slice(0, maxIconsToDisplay)
            .concat(
              <span className="NewSmallIcon-moreAction">
                +{iconsToRender.length - maxIconsToDisplay}
              </span>
            )
        : iconsToRender;
    };

    if (noParentWrapper) {
      return (
        <>
          {renderDot && (
            <Typography variant="overline" color="subtle">
              {`·`}
            </Typography>
          )}
          {renderIcons()}
        </>
      );
    }

    return <div className="PartyBody-detailsRow--tags NewSmallIcon-Container">{renderIcons()}</div>;
  }
}

export default connect<Props, ConnectProps>(
  withFeatureFlags(GuestAndVisitIcons),
  (state, props) => {
    return {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      processedVisitTags: mapStringToVisitTagsSelector(state, props!),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      processedGuestTags: mapStringToGuestTagsSelector(state, props!),
      activeBusinessId: oc(state).app.activeBusiness.id(0),
    };
  }
);
